import Image from "next/image";
import { Eyebrow, displayFont } from "@/components/content-blocks/band-helpers";
import { resolveMediaAlt } from "@/lib/product-display";
import type { StorefrontSiteCode } from "@/lib/storefront-site";
import type { CategorySummary } from "@/lib/storefront-types";

type CategoryPageIntroProps = {
  category: CategorySummary;
  productCount: number;
  siteCode: StorefrontSiteCode;
};

export function CategoryPageIntro({
  category,
  productCount,
  siteCode,
}: CategoryPageIntroProps) {
  const hasImage = Boolean(category.image?.url);
  // Brand-correct fulfilment chip: Fresh is London delivery/collection, Frozen
  // ships the same chef-cooked dishes UK-wide (owner-approved wording).
  const fulfilmentNote =
    siteCode === "asorockfrozen"
      ? "Chef-cooked, flash-frozen and delivered UK-wide"
      : "Freshly prepared for delivery or collection";

  return (
    <section className="storefront-page-intro-background relative overflow-hidden border-y storefront-border-soft">
      <div className="storefront-soft-radial-top-right pointer-events-none absolute inset-0" />

      <div className="relative mx-auto grid max-w-6xl gap-6 px-4 py-7 sm:px-6 sm:py-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(16rem,20rem)] lg:items-center lg:gap-10 lg:py-10">
        <div className="space-y-4 text-center lg:text-left">
          <Eyebrow className="storefront-text-muted">Menu category</Eyebrow>
          <div className="space-y-3">
            <h1
              className={`${displayFont.className} text-[clamp(1.85rem,3.6vw,3.2rem)] font-bold leading-[1.06] tracking-tight storefront-text-heading`}
            >
              {category.name}
            </h1>
            {category.summary ? (
              <p className="mx-auto max-w-[42rem] text-[0.98rem] leading-7 storefront-text-body sm:text-[1.05rem] lg:mx-0">
                {category.summary}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-center gap-2 pt-1 lg:justify-start lg:gap-3">
            <span className="inline-flex items-center rounded-full border storefront-border-soft bg-white/86 px-3 py-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[#184a22] sm:px-4 sm:py-2 sm:text-xs sm:tracking-[0.18em]">
              {productCount} {productCount === 1 ? "dish" : "dishes"} available
            </span>
            <span className="storefront-bg-primary-soft storefront-text-primary inline-flex items-center rounded-full px-3 py-1.5 text-[0.65rem] font-medium sm:px-4 sm:py-2 sm:text-xs">
              {fulfilmentNote}
            </span>
          </div>
        </div>

        <div className="hidden lg:block lg:justify-self-end">
          <div className="relative overflow-hidden rounded-[1.6rem] border border-white/80 bg-white/92 shadow-[0_24px_48px_-32px_rgba(0,39,0,0.24)]">
            <div className="storefront-soft-divider-horizontal absolute inset-x-5 top-0 h-px" />
            <div className="flex min-h-[12.5rem] w-full min-w-[15.5rem] items-center justify-center bg-white p-4 sm:min-h-[14rem] sm:min-w-[17.5rem] sm:p-5">
              {hasImage ? (
                <div className="relative h-[10rem] w-full sm:h-[11rem]">
                  <Image
                    src={category.image!.url}
                    alt={resolveMediaAlt(
                      category.image!.alt,
                      `${category.name} category image`,
                    )}
                    fill
                    sizes="(min-width: 1024px) 18rem, (min-width: 640px) 40vw, 80vw"
                    className="object-contain"
                  />
                </div>
              ) : (
                <div className="space-y-2 text-center">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] storefront-text-muted">
                    Aso Rock menu
                  </p>
                  <p className="text-2xl font-semibold tracking-tight storefront-text-heading">
                    {category.name}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
